import { REMAINING_PROP_TYPES } from './remaining-town-art';
export const ROAD_SPRITE_KEY = 'terrain.road.v1';
const DEFAULT_TERRAIN_KEY = 'terrain.grass.v1';
const HERO_MODEL_VARIANTS = [0, 1, 2, 3] as const;

/** Wilderness rest: separate props (transparent PNG), tent ~30px art height. */
export const CAMP_TENT_TEXTURE_KEY = 'prop.camp_tent.v0';
export const CAMP_FIRE_TEXTURE_KEY = 'prop.camp_fire.v0';
export const CAMP_BAG_TEXTURE_KEY = 'prop.camp_bag.v0';

const TERRAIN_TEXTURE_BY_KEY: Record<string, string> = {
  plaza: 'terrain.plaza.v1',
  road: ROAD_SPRITE_KEY,
  dirt: 'terrain.dirt.v1',
  stone: 'terrain.stone.v1',
  forest_floor: 'terrain.forest_floor.v1',
  ruins_floor: 'terrain.ruins_floor.v1',
  canyon_floor: 'terrain.canyon_floor.v1',
  swamp_floor: 'terrain.swamp_floor.v1',
  water: 'terrain.river_water.v1',
  river_shore: 'terrain.river_shore.v1',
  volcanic_floor: 'terrain.volcanic_floor.v1',
  astral_floor: 'terrain.astral_floor.v1',
  winter_floor: 'terrain.winter_floor.v1',
  jungle_floor: 'terrain.jungle_floor.v1',
  grass: DEFAULT_TERRAIN_KEY,
};

const FIVE_VARIANT_KEYS = (prefix: string) =>
  [`${prefix}.v0`, `${prefix}.v1`, `${prefix}.v2`, `${prefix}.v3`, `${prefix}.v4`] as const;

/** Manifest texture keys per procedural object type (variant index 0..N-1). */
const OBJECT_TEXTURE_VARIANTS: Record<string, readonly string[]> = {
  ...Object.fromEntries(Object.keys(REMAINING_PROP_TYPES).map(family => [family, [`prop.authored.${family}`]])),
  meadow_training_dummy:['prop.authored.meadow_training_dummy'],meadow_weapon_rack:['prop.authored.meadow_weapon_rack'],
  meadow_fountain:['prop.authored.meadow_fountain'],meadow_well:['prop.authored.meadow_well'],meadow_market_stall:['prop.authored.meadow_market_stall'],
  ...Object.fromEntries(['civic_tree','oak','pine','birch','bush'].map(t=>[`meadow_${t}`,[`prop.authored.meadow_${t}`]])),
  forest_fountain:['prop.authored.forest_fountain'],forest_well:['prop.authored.forest_well'],
  canyon_fountain:['prop.authored.canyon_fountain'],canyon_well:['prop.authored.canyon_well'],
  swamp_fountain:['prop.authored.swamp_fountain'],swamp_well:['prop.authored.swamp_well'],
  canyon_market_stall:['prop.authored.canyon_market_stall'],
  forest_oak: ['prop.tree.v0'],
  forest_pine: ['prop.tree.v1'],
  forest_birch: ['prop.tree.v2'],
  forest_bush: ['prop.bush.v0'],
  canyon_rock: ['prop.rock.v0'],
  cactus: ['prop.cactus.v0', 'prop.cactus.v1'],
  dry_stump: ['prop.stump.v0'],
  swamp_tree: ['prop.tree.swamp'],
  swamp_stump: ['prop.stump.v0'],
  market_stall: ['prop.market.stall.v0'],
  sacks: ['prop.sacks.v0'],
  cargo_sacks: ['prop.cargo_sacks.v0'],
  cargo_wheel: ['prop.cargo_wheel.v0'],
  road_signpost: ['prop.road_signpost.v1'],
  forest_mushrooms: ['prop.forest_mushrooms.v0'],
  ruin_rubble: ['prop.ruin_rubble.v0'],
  field_hay_bale: ['prop.field_hay_bale.v0'],
  river_water_tile: ['terrain.river_water.v1'],
  river_pond: ['terrain.river_pond.v1'],
  debris: ['prop.debris.v0'],
  swamp_log: ['prop.swamp_log.v1'],
  civic_tree: ['prop.civic_tree.v0'],
  noticeboard: ['prop.noticeboard.v0'],
  stone_flowerbed: ['prop.stone_flowerbed.v0'],
  swamp_stepping_stones: ['prop.swamp_stepping_stones.v1'],
  marsh_reeds: ['prop.marsh_reeds.v2'],
  dock_segment: ['prop.dock_segment.v1'],
  fishing_rod: ['prop.fishing_rod.v1'],
  fishing_net: ['prop.fishing_net.v1'],
  fallen_horse: ['prop.fallen_horse.v2', 'prop.fallen_horse.v3'],
  wayside_shrine: ['prop.wayside_shrine.v1'],
  water_lilies: ['prop.water_lilies.v1'],
  volcanic_basalt: ['prop.volcanic_basalt.v1', 'prop.volcanic_basalt.v2'],
  skeleton_remains: ['prop.skeleton_remains.v1'],
  rusted_swords: ['prop.rusted_swords.v1'],
  ruin_column: ['prop.ruin_column.v1'],
  ruin_arch_fragment: ['prop.ruin_arch_fragment.v1'],
  road_milestone: ['prop.road_milestone.v1'],
  riverbank_rocks: ['prop.swamp_stepping_stones.v1'],
  small_stone_cluster: ['prop.small_stone_cluster.v1'],
  river_driftwood: ['prop.fallen_log.v1'],
  meadow_wildflowers: ['prop.meadow_wildflowers.v1'],
  harvest_sheaves: ['prop.harvest_sheaves.v1'],
  field_scarecrow: ['prop.field_scarecrow.v1'],
  crop_patch: ['prop.crop_patch.v1'],
  canyon_thorns: ['prop.canyon_thorns.v1'],
  bog_lights: ['prop.bog_lights.v1'],
  astral_crystals: ['prop.astral_crystals.v1'],
  fallen_log: ['prop.fallen_log.v1'],
  underbrush_cluster: ['prop.underbrush_cluster.v1'],
  camp_tent: ['prop.camp_tent.v0'],
  camp_fire: ['prop.camp_fire.v0'],
  camp_bag: ['prop.camp_bag.v0'],
  candle_cluster: ['prop.candles.v0'],
  beehive: ['prop.beehive.v0'],
  hunting_trap: ['prop.hunting_trap.v0'],
  minecart: ['prop.minecart.v0'],
  mill_wheel: ['prop.mill_wheel.v0'],
  stone_well: ['prop.stone_well.v0'],
  snow_drift: ['prop.snow_drift.v0'],
  ruin_vines: ['prop.ruin_vines.v0'],
  gallows: ['prop.gallows.v0'],
  rowboat: ['prop.rowboat.v0'],
  mine_tools: ['prop.mine_tools_flat.v1'],
  mine_entrance: ['prop.mine_entrance.v1'],
  mine_rocky_flank: ['prop.mine_rocky_flank.v1'],
  overgrown_temple: ['prop.overgrown_temple.v1'],
  woodland_shrine: ['prop.woodland_shrine.v1'],
  ruin_pediment_fragment: ['prop.ruin_pediment_fragment.v1'],
  ruin_statue_fragment: ['prop.ruin_statue_fragment.v1'],
  ruin_roof_rubble: ['prop.ruin_roof_rubble.v1'],
  broken_wayside_hut: ['prop.broken_wayside_hut.v1'],
  watchtower_remnant: ['prop.watchtower_remnant.v1'],
  camp_tent_reverse: ['prop.camp_tent.v1'],
  scene_tent_west: ['prop.scene_tent_west.v1'],
  scene_tent_east: ['prop.scene_tent_east.v1'],
  scene_tent_green: ['prop.scene_tent_green.v1'],
  broken_caravan_wagon: ['prop.broken_caravan_wagon.v1'],
  desiccated_guard: ['prop.desiccated_guard.v2', 'prop.desiccated_guard.v1'],
  astral_ground_rift: ['prop.astral_ground_rift.v1'],
  stuck_arrows: ['prop.stuck_arrows.v1', 'prop.stuck_arrows.v2'],
  stuck_spear: ['prop.stuck_spear.v1'],
  unlit_torch: ['prop.unlit_torch.v1', 'prop.unlit_torch.v2', 'prop.unlit_torch.v3'],
  fallen_log_straight: ['prop.fallen_log_straight.v1'],
  fallen_log_forked: ['prop.fallen_log_forked.v1'],
  fallen_log_rootball: ['prop.fallen_log_rootball.v1'],
  fallen_log_hollow: ['prop.fallen_log_hollow.v1'],
  swamp_berry_bush: ['prop.swamp_berry_bush.v1'],
  shield_arrows: ['prop.shield_arrows.v0'],
  rope_coil: ['prop.rope_coil.v0'],
  broken_ladder: ['prop.broken_ladder.v0'],
  nest_feathers: ['prop.nest_feathers.v0'],
  bog_hummock: ['prop.bog_hummock.v1'],
  cold_camp_fire: ['prop.cold_ash_campfire.v1'],
  backpack_quiver: ['prop.backpack_quiver.v1'],
  shepherd_staff: ['prop.shepherd_staff.v1'],
  ore_chunks: ['prop.ore_chunks.v1'],
  broken_beehive: ['prop.broken_beehive.v1'],
  offering_bowl_dagger: ['prop.offering_bowl_dagger.v1'],
  tree: FIVE_VARIANT_KEYS('prop.tree'),
  rock: FIVE_VARIANT_KEYS('prop.rock'),
  cart: FIVE_VARIANT_KEYS('prop.cart'),
  broken_cart: ['prop.broken_cart.v0'],
  cargo_crate: ['prop.cargo_crate.v0'],
  cargo_sheaf: ['prop.cargo_sheaf.v0'],
  detached_skull: ['prop.detached_skull.v1'],
  barrel: FIVE_VARIANT_KEYS('prop.barrel'),
  crate: FIVE_VARIANT_KEYS('prop.crate'),
  bush: FIVE_VARIANT_KEYS('prop.bush'),
  mushroom: FIVE_VARIANT_KEYS('prop.mushroom'),
  leaves: FIVE_VARIANT_KEYS('prop.leaves'),
  stump: FIVE_VARIANT_KEYS('prop.stump'),
  bones: FIVE_VARIANT_KEYS('prop.bones'),
  ruin: FIVE_VARIANT_KEYS('prop.ruin'),
  lantern: FIVE_VARIANT_KEYS('prop.lantern'),
  bench: FIVE_VARIANT_KEYS('prop.bench'),
  hay_bale: FIVE_VARIANT_KEYS('prop.hay_bale'),
  wagon_wheel: FIVE_VARIANT_KEYS('prop.wagon_wheel'),
  shrine: FIVE_VARIANT_KEYS('prop.shrine'),
  planter: FIVE_VARIANT_KEYS('prop.planter'),
};

/** Max variant index (inclusive) for town editor / server placement. */
export const OBJECT_VARIANT_MAX: Record<string, number> = {
  training_dummy:0,weapon_rack:0,
  market_stall: 0,
  sacks: 0,
  cargo_sacks: 0,
  cargo_wheel: 0,
  cold_camp_fire: 0,
  backpack_quiver: 0,
  shepherd_staff: 0,
  ore_chunks: 0,
  broken_beehive: 0,
  offering_bowl_dagger: 0,
  mine_entrance: 0,
  mine_rocky_flank: 0,
  overgrown_temple: 0,
  woodland_shrine: 0,
  ruin_pediment_fragment: 0,
  ruin_statue_fragment: 0,
  ruin_roof_rubble: 0,
  broken_wayside_hut: 0,
  watchtower_remnant: 0,
  camp_tent_reverse: 0,
  scene_tent_west: 0,
  scene_tent_east: 0,
  scene_tent_green: 0,
  broken_caravan_wagon: 0,
  desiccated_guard: 1,
  astral_ground_rift: 0,
  stuck_arrows: 1,
  stuck_spear: 0,
  unlit_torch: 2,
  fallen_log_straight: 0,
  fallen_log_forked: 0,
  fallen_log_rootball: 0,
  fallen_log_hollow: 0,
  swamp_berry_bush: 0,
  bog_hummock: 0,
  road_signpost: 0,
  forest_mushrooms: 0,
  ruin_rubble: 0,
  field_hay_bale: 0,
  river_water_tile: 0,
  river_pond: 0,
  debris: 0,
  swamp_log: 0,
  civic_tree: 0,
  noticeboard: 0,
  stone_flowerbed: 0,
  swamp_stepping_stones: 0,
  marsh_reeds: 0,
  dock_segment: 0,
  fishing_rod: 0,
  fishing_net: 0,
  fallen_horse: 1,
  wayside_shrine: 0,
  water_lilies: 0,
  volcanic_basalt: 1,
  skeleton_remains: 0,
  rusted_swords: 0,
  ruin_column: 0,
  ruin_arch_fragment: 0,
  road_milestone: 0,
  riverbank_rocks: 0,
  small_stone_cluster: 0,
  river_driftwood: 0,
  meadow_wildflowers: 0,
  harvest_sheaves: 0,
  field_scarecrow: 0,
  crop_patch: 0,
  canyon_thorns: 0,
  cactus: 1,
  bog_lights: 0,
  astral_crystals: 0,
  fallen_log: 0,
  underbrush_cluster: 0,
  camp_tent: 0,
  camp_fire: 0,
  camp_bag: 0,
  candle_cluster: 0,
  beehive: 0,
  hunting_trap: 0,
  minecart: 0,
  mill_wheel: 0,
  stone_well: 0,
  snow_drift: 0,
  ruin_vines: 0,
  gallows: 0,
  rowboat: 0,
  mine_tools: 0,
  shield_arrows: 0,
  rope_coil: 0,
  broken_ladder: 0,
  nest_feathers: 0,
  tree: 4,
  rock: 4,
  cart: 4,
  broken_cart: 0,
  cargo_crate: 0,
  cargo_sheaf: 0,
  detached_skull: 0,
  barrel: 4,
  crate: 4,
  bush: 4,
  mushroom: 4,
  leaves: 4,
  stump: 4,
  bones: 4,
  ruin: 4,
  lantern: 4,
  bench: 4,
  hay_bale: 4,
  wagon_wheel: 4,
  shrine: 4,
  planter: 4,
};

const BUILDING_TEXTURE_VARIANTS: Record<string, readonly string[]> = {
  hovel: Array.from({length:6},(_,i)=>`building.authored.hovel_${i}`),
  house: FIVE_VARIANT_KEYS('building.house'),
  inn: FIVE_VARIANT_KEYS('building.inn'),
  roadside_tavern: FIVE_VARIANT_KEYS('building.roadside_tavern'),
  blacksmith: FIVE_VARIANT_KEYS('building.blacksmith'),
  chapel: FIVE_VARIANT_KEYS('building.chapel'),
  warehouse: FIVE_VARIANT_KEYS('building.warehouse'),
  townhall: FIVE_VARIANT_KEYS('building.civic.townhall'),
  fountain: FIVE_VARIANT_KEYS('prop.fountain.plaza'),
  stall: FIVE_VARIANT_KEYS('prop.market.stall'),
  signpost: FIVE_VARIANT_KEYS('prop.signpost'),
};

/** Default preload keys (variant 0). */
export const TOWN_HALL_TEXTURE_KEY = 'building.civic.townhall.v0';
export const PLAZA_FOUNTAIN_TEXTURE_KEY = 'prop.fountain.plaza.v0';
export const MARKET_STALL_TEXTURE_KEY = 'prop.market.stall.v0';
export const SIGNPOST_TEXTURE_KEY = 'prop.signpost.v0';

/** Generic facades for procedural town layouts (no `town_buildings` rows). */
export const PROCEDURAL_TOWN_HOUSE_FACADE_KEYS = FIVE_VARIANT_KEYS('building.house');

function pickVariantKey(keys: readonly string[], variant: number): string | null {
  if (!keys.length) return null;
  if (Number.isInteger(variant) && variant >= 0 && variant < keys.length) {
    return keys[variant] ?? null;
  }
  const idx = Math.min(keys.length - 1, Math.floor(variant * keys.length));
  return keys[idx] ?? null;
}

/** Maps semantic building types to distinct architectural families. */
export function buildingTextureFamily(buildingType: string): string | null {
  if (buildingType === 'house.hovel') return 'hovel';
  if (buildingType === 'house.inn') return 'inn';
  if (buildingType === 'house.tavern') return 'roadside_tavern';
  if (buildingType === 'house.weapon_smith' || buildingType === 'house.armorer') return 'blacksmith';
  if (buildingType === 'house.healer') return 'chapel';
  if (buildingType === 'house.merchant') return 'warehouse';
  if (buildingType.startsWith('house.')) return 'house';
  if (buildingType === 'decoration.well') return 'fountain';
  if (buildingType === 'decoration.stall') return 'stall';
  if (buildingType === 'decoration.signpost') return 'signpost';
  if (buildingType === 'decoration.townhall') return 'townhall';
  return null;
}

export function terrainToTextureKey(terrain: string): string {
  return TERRAIN_TEXTURE_BY_KEY[terrain] ?? DEFAULT_TERRAIN_KEY;
}

export function objectToTextureKey(obj: string, variant: number): string | null {
  if (obj === 'signpost') return 'prop.signpost.v0';
  const keys = OBJECT_TEXTURE_VARIANTS[obj];
  if (!keys?.length) return null;
  return pickVariantKey(keys, variant);
}

export function objectVariantCount(obj: string): number {
  return OBJECT_TEXTURE_VARIANTS[obj]?.length ?? 0;
}

/** Hero source textures are 112px tall; renderer scales heroes to this on-screen height. */
export const CHARACTER_TEXTURE_HEIGHT_PX = 112;
export const CHARACTER_DISPLAY_HEIGHT_PX = 90;

/** @deprecated Use characterSpriteScale() â€” fixed ratio only valid for 112px hero textures. */
export const HERO_RENDERER_SCALE = CHARACTER_DISPLAY_HEIGHT_PX / CHARACTER_TEXTURE_HEIGHT_PX;

/** Scale factor for bottom-anchored hero sprites (display height / texture height). NPCs render at native scale (1). */
export function characterSpriteScale(textureHeightPx: number): number {
  const h = Math.max(1, textureHeightPx || CHARACTER_TEXTURE_HEIGHT_PX);
  return CHARACTER_DISPLAY_HEIGHT_PX / h;
}

/** Display height multipliers vs hero texture â€” legacy fallback sizing; canonical presentation metadata takes precedence. */
export const OBJECT_SIZE_TIER = {
  tiny: 0.75,
  small: 1.5,
  medium: 2,
  large: 2.75,
  huge: 4,
} as const;

export type ObjectSizeTier = keyof typeof OBJECT_SIZE_TIER;

/** Procedural / town-editor object types â†’ size tier. */
const PROP_SIZE_TIER: Record<string, ObjectSizeTier> = {
  bench: 'small',
  bush: 'small',
  signpost: 'small',
  barrel: 'small',
  cart: 'small',
  lantern: 'small',
  crate: 'small',
  rock: 'small',
  stump: 'small',
  wagon_wheel: 'medium',
  hay_bale: 'large',
  shrine: 'large',
  tree: 'large',
  ruin: 'medium',
  bones: 'tiny',
  planter: 'tiny',
  leaves: 'tiny',
  mushroom: 'tiny',
};

export function objectSizeTier(objectType: string): ObjectSizeTier {
  return PROP_SIZE_TIER[objectType] ?? 'small';
}

export function displayHeightForTier(tier: ObjectSizeTier, townScale = 1): number {
  return CHARACTER_TEXTURE_HEIGHT_PX * OBJECT_SIZE_TIER[tier] * townScale;
}

/** Art-generation target height (hero texture px Ã— tier). Existing PNGs are already sized â€” do not force this at runtime. */
export function propObjectGenerationHeight(objectType: string, townScale = 1): number {
  return displayHeightForTier(objectSizeTier(objectType), townScale);
}

/** @deprecated Use propObjectGenerationHeight â€” name was misleading for runtime. */
export function propObjectDisplayHeight(objectType: string, townScale = 1): number {
  return propObjectGenerationHeight(objectType, townScale);
}

/** Server / procedural building types â†’ art-generation size tier. */
export function buildingSizeTier(buildingType: string): ObjectSizeTier {
  if (buildingType === 'decoration.well') return 'large';
  if (buildingType === 'decoration.stall') return 'medium';
  if (buildingType === 'decoration.signpost') return 'small';
  if (buildingType.startsWith('house.') || buildingType === 'decoration.townhall') return 'huge';
  return 'huge';
}

/** Art-generation target for buildings. */
export function buildingGenerationHeight(buildingType: string, townScale = 1): number {
  return displayHeightForTier(buildingSizeTier(buildingType), townScale);
}

/** @deprecated Use buildingGenerationHeight */
export function buildingDisplayHeight(buildingType: string, townScale = 1): number {
  return buildingGenerationHeight(buildingType, townScale);
}

/** Resolve building sprite from type + deterministic variant float or integer index. */
export function buildingTypeToTextureKey(buildingType: string, variant = 0): string | null {
  const family = buildingTextureFamily(buildingType);
  if (!family) return null;
  const keys = BUILDING_TEXTURE_VARIANTS[family];
  return pickVariantKey(keys ?? [], variant);
}

export function buildingVariantCount(buildingType: string): number {
  const family = buildingTextureFamily(buildingType);
  if (!family) return 0;
  return BUILDING_TEXTURE_VARIANTS[family]?.length ?? 0;
}

/** Legacy hash used before explicit variant was stored; still used as fallback. */
export function buildingVariantHash(id: number, worldX: number, worldY: number): number {
  return (
    (((id * 1315423911) ^ (Math.floor(worldX * 17) << 1) ^ Math.floor(worldY * 31)) >>> 0) /
    0x7fffffff
  );
}

export function resolveBuildingSpriteVariant(
  id: number,
  worldX: number,
  worldY: number,
  variant: number | undefined,
): number {
  if (variant !== undefined && Number.isInteger(variant) && variant >= 0) {
    return variant;
  }
  return buildingVariantHash(id, worldX, worldY);
}

export type HeroFacing = 'south' | 'east' | 'north' | 'west' | 'south-east' | 'north-east' | 'north-west' | 'south-west';

export function heroTextureKey(modelVariant: number, facing: HeroFacing): string {
  const variant = Number.isInteger(modelVariant) && modelVariant >= 0 && modelVariant <= 3
    ? modelVariant
    : 0;
  return `hero.player.v${variant}.${facing}`;
}

export function restCampTextureKeys(): [string, string, string] {
  return [CAMP_TENT_TEXTURE_KEY, CAMP_FIRE_TEXTURE_KEY, CAMP_BAG_TEXTURE_KEY];
}

/**
 * Normalizes `enemies.type` for texture keys: trim, lowercase, strip accidental `enemy.` / `.south`.
 */
export function normalizeEnemyTemplateSlug(slug: string): string {
  let s = String(slug).trim().toLowerCase().replace(/\s+/g, '_');
  if (s.startsWith('enemy.')) s = s.slice(6);
  if (s.endsWith('.south')) s = s.slice(0, -6);
  return s;
}

/** South-facing manifest key for a template slug (`enemy.<slug>.south`). */
export function enemySouthTextureKey(slug: string): string {
  return `enemy.${normalizeEnemyTemplateSlug(slug)}.south`;
}

/** If exact south PNG is missing from the bundle, try another bandit variant (same archetype art). */
const BANDIT_SOUTH_FALLBACK_SLUGS: readonly string[] = [
  'bandit_l8_9_canyon',
  'bandit_l10_11_canyon',
  'bandit_l8_9_ruins',
  'bandit_l6_7_ruins',
  'bandit_l6_7_forest',
  'bandit_l4_5_forest',
  'bandit_l4_5_meadow',
  'bandit_l10_11_swamp',
  'bandit_l12_12_volcanic',
  'bandit_l12_12_astral',
];

/**
 * Resolves a loaded south-facing enemy texture key, or null to use procedural fallback.
 */
export function resolveEnemySouthTextureKey(
  slug: string,
  getTexture: (manifestKey: string) => unknown | null,
): string | null {
  const norm = normalizeEnemyTemplateSlug(slug);
  const trySlug = (templateSlug: string): string | null => {
    const withSouth = `enemy.${templateSlug}.south`;
    if (getTexture(withSouth) != null) return withSouth;
    const bare = `enemy.${templateSlug}`;
    return getTexture(bare) != null ? bare : null;
  };

  const primary = trySlug(norm);
  if (primary) return primary;

  if (!norm.includes('bandit')) return null;

  for (const s of BANDIT_SOUTH_FALLBACK_SLUGS) {
    if (s === norm) continue;
    const k = trySlug(s);
    if (k) return k;
  }
  return null;
}

export function getRequiredSpriteKeys(): string[] {
  const terrainKeys = Object.values(TERRAIN_TEXTURE_BY_KEY);
  const objectKeys = Object.values(OBJECT_TEXTURE_VARIANTS).flatMap((keys) => [...keys]);
  const buildingKeys = Object.values(BUILDING_TEXTURE_VARIANTS).flatMap((keys) => [...keys]);
  return [
    ...new Set([
      ...terrainKeys,
      ...objectKeys,
      ...buildingKeys,
      ...HERO_MODEL_VARIANTS.flatMap((v) => [`hero.player.v${v}.south`, `hero.player.v${v}.north`]),
      CAMP_TENT_TEXTURE_KEY,
      CAMP_FIRE_TEXTURE_KEY,
      CAMP_BAG_TEXTURE_KEY,
    ]),
  ];
}
